import Cookies from 'js-cookie';

import { ALL_ATTRIBUTION_KEYS, SIGNUP_ATTRIBUTION_KEYS } from '@/shared/constants/attribution';
import { LS_UTM_PARAMS } from '@/shared/constants/localStorage';

import { isTrackingDisabled } from './isTrackingDisabled';

export type AttributionParams = Partial<Record<(typeof ALL_ATTRIBUTION_KEYS)[number], string>>;

export type GA4Context = {
  clientId: string;
  sessionId: string;
};

export type GoogleAdsAttribution = {
  captured_at: string;
  gbraid: null | string;
  gclid: null | string;
  wbraid: null | string;
};

export type GoogleAdsIdentifiers = Omit<GoogleAdsAttribution, 'captured_at'>;

export type LinkedInAttribution = {
  captured_at: string;
  li_fat_id: string;
};

/**
 * Type sent to backend on signup. Backend uses set-once semantics —
 * fields not present in payload are not touched on existing user records.
 */
export type AttributionPayloadForSignup = {
  [K in (typeof SIGNUP_ATTRIBUTION_KEYS)[number] as `first_${K}`]?: string;
} & {
  ga4_client_id?: string;
  ga4_session_id?: string;
} & Partial<GoogleAdsAttribution>;

const GA4_SESSION_COOKIE_NAME = '_ga_R60NZTEKKL';
const GOOGLE_ADS_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const GOOGLE_ADS_CLICK_ID_PATTERN = /^[A-Za-z0-9._~-]{1,2048}$/;
const LINKEDIN_CLICK_ID_KEY = 'li_fat_id';
const LINKEDIN_CLICK_ID_PATTERN = /^[A-Za-z0-9._~-]{1,256}$/;

const getGoogleAdsIdentifierParams = (value: AttributionParams): AttributionParams => {
  const identifiers: AttributionParams = {};
  for (const key of GOOGLE_ADS_CLICK_ID_KEYS) {
    const identifier = value[key];
    if (identifier && GOOGLE_ADS_CLICK_ID_PATTERN.test(identifier)) identifiers[key] = identifier;
  }
  return identifiers;
};

const getGoogleAdsIdentifiersFromAttribution = (value: AttributionParams): GoogleAdsIdentifiers | undefined => {
  const identifiers = getGoogleAdsIdentifierParams(value);
  if (Object.keys(identifiers).length === 0) return undefined;
  return {
    gbraid: identifiers.gbraid ?? null,
    gclid: identifiers.gclid ?? null,
    wbraid: identifiers.wbraid ?? null,
  };
};

const normalizeLinkedInClickId = (value: null | string | undefined) => {
  const normalized = value?.trim();

  return normalized && LINKEDIN_CLICK_ID_PATTERN.test(normalized) ? normalized : undefined;
};

const filterAttributionParams = (value: unknown): AttributionParams => {
  if (!value || typeof value !== 'object') return {};

  const filtered: AttributionParams = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    const candidate = (value as Record<string, unknown>)[key];
    const isGoogleAdsClickId = GOOGLE_ADS_CLICK_ID_KEYS.includes(key as (typeof GOOGLE_ADS_CLICK_ID_KEYS)[number]);
    if (
      typeof candidate === 'string' &&
      candidate &&
      (!isGoogleAdsClickId || GOOGLE_ADS_CLICK_ID_PATTERN.test(candidate)) &&
      (key !== LINKEDIN_CLICK_ID_KEY || LINKEDIN_CLICK_ID_PATTERN.test(candidate))
    ) {
      filtered[key] = candidate;
    }
  }
  return filtered;
};

const captureFromSearch = (search: string): AttributionParams => {
  const urlParams = new URLSearchParams(search);
  const params: AttributionParams = {};
  for (const key of ALL_ATTRIBUTION_KEYS) {
    const value = urlParams.get(key);
    const isGoogleAdsClickId = GOOGLE_ADS_CLICK_ID_KEYS.includes(key as (typeof GOOGLE_ADS_CLICK_ID_KEYS)[number]);
    if (
      value &&
      (!isGoogleAdsClickId || GOOGLE_ADS_CLICK_ID_PATTERN.test(value)) &&
      (key !== LINKEDIN_CLICK_ID_KEY || LINKEDIN_CLICK_ID_PATTERN.test(value))
    ) {
      params[key] = value;
    }
  }
  return params;
};

export const buildAttributionSnapshot = ({ search }: { search: string }): AttributionParams =>
  captureFromSearch(search);

const GA_LINKER_PARAM = '_gl';
const REDIRECT_PRESERVED_PARAMS = new Set<string>([GA_LINKER_PARAM, ...ALL_ATTRIBUTION_KEYS]);

export const pickAttributionSearch = (search: string): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (REDIRECT_PRESERVED_PARAMS.has(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
};

/**
 * Read stored attribution params from localStorage and re-apply the allowlist.
 * Falls back to empty object if nothing is stored or JSON is invalid.
 */
export const getStoredAttributionParams = (): AttributionParams => {
  try {
    const stored = window.localStorage.getItem(LS_UTM_PARAMS);
    return stored ? filterAttributionParams(JSON.parse(stored)) : {};
  } catch {
    return {};
  }
};

export const getGoogleAdsIdentifiers = (): GoogleAdsIdentifiers | undefined => {
  if (isTrackingDisabled()) return undefined;
  return getGoogleAdsIdentifiersFromAttribution(getStoredAttributionParams());
};

export const getLinkedInClickId = (): string | undefined => {
  if (isTrackingDisabled()) return undefined;

  const queryValue = normalizeLinkedInClickId(new URLSearchParams(window.location.search).get(LINKEDIN_CLICK_ID_KEY));
  if (queryValue) return queryValue;

  const cookieValue = normalizeLinkedInClickId(Cookies.get(LINKEDIN_CLICK_ID_KEY));
  if (cookieValue) return cookieValue;

  return normalizeLinkedInClickId(getStoredAttributionParams()[LINKEDIN_CLICK_ID_KEY]);
};

export const getLinkedInAttribution = (): LinkedInAttribution | undefined => {
  const clickId = getLinkedInClickId();
  return clickId ? { captured_at: new Date().toISOString(), li_fat_id: clickId } : undefined;
};

/**
 * Capture the filtered campaign snapshot from URL into localStorage. URL
 * attribution is always a complete new snapshot. Returns it for immediate use.
 */
export const captureAttributionParamsFromUrl = (): AttributionParams => {
  if (isTrackingDisabled()) return {};

  const stored = getStoredAttributionParams();
  const captured = captureFromSearch(window.location.search);
  const cookieLinkedInClickId = normalizeLinkedInClickId(Cookies.get(LINKEDIN_CLICK_ID_KEY));
  if (!captured.li_fat_id && cookieLinkedInClickId) captured.li_fat_id = cookieLinkedInClickId;
  if (Object.keys(captured).length === 0) return {};

  const newGoogleAdsIdentifiers = getGoogleAdsIdentifiersFromAttribution(captured);
  const storedGoogleAdsIdentifiers = getGoogleAdsIdentifierParams(stored);
  const params = newGoogleAdsIdentifiers ? captured : { ...storedGoogleAdsIdentifiers, ...captured };
  const storedLinkedInClickId = normalizeLinkedInClickId(stored.li_fat_id);
  if (!params.li_fat_id && storedLinkedInClickId) params.li_fat_id = storedLinkedInClickId;

  try {
    window.localStorage.setItem(LS_UTM_PARAMS, JSON.stringify(params));
  } catch {}

  return params;
};

/**
 * Read GA4 client_id from the _ga cookie.
 * Format: GA1.1.XXXXXXXXXX.XXXXXXXXXX → client_id = last two parts joined by dot.
 */
export const getGA4ClientId = (): string | undefined => {
  try {
    const gaCookie = Cookies.get('_ga');
    if (!gaCookie) return undefined;

    const parts = gaCookie.split('.');

    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  } catch {}

  return undefined;
};

export const getGA4SessionId = (): string | undefined => {
  try {
    const value = Cookies.get(GA4_SESSION_COOKIE_NAME);
    if (!value) return undefined;

    const gs2SessionId = /\.s(\d+)(?:\$|$)/.exec(value)?.[1];
    if (gs2SessionId) return gs2SessionId;

    return /^GS\d+\.\d+\.(\d+)(?:\.|$)/.exec(value)?.[1];
  } catch {}

  return undefined;
};

export const getGA4Context = (): GA4Context | undefined => {
  if (isTrackingDisabled()) return undefined;

  const clientId = getGA4ClientId();
  const sessionId = getGA4SessionId();
  return clientId && sessionId ? { clientId, sessionId } : undefined;
};

/**
 * Assemble attribution payload for the signup API call.
 * Includes only fields with values — backend uses set-once semantics,
 * so missing fields don't overwrite existing values on the user record.
 */
export const getAttributionPayloadForSignup = (): AttributionPayloadForSignup => {
  const stored = getStoredAttributionParams();
  const payload: AttributionPayloadForSignup = {};

  for (const key of SIGNUP_ATTRIBUTION_KEYS) {
    const value = stored[key];
    if (value) {
      (payload as Record<string, string>)[`first_${key}`] = value;
    }
  }

  const clientId = getGA4ClientId();
  if (clientId) payload.ga4_client_id = clientId;

  const sessionId = getGA4SessionId();
  if (sessionId) payload.ga4_session_id = sessionId;

  const googleAdsIdentifiers = getGoogleAdsIdentifiers();
  if (googleAdsIdentifiers) {
    Object.assign(payload, googleAdsIdentifiers, { captured_at: new Date().toISOString() });
  }

  return payload;
};
